import {
  createContext,
  useContext,
  ReactNode,
  useState,
  Dispatch,
  SetStateAction,
} from "react";
import { useNavigate } from "react-router-dom";
import { PrpNavigationItemType } from ".";
import { useMediaQuery, useTheme } from "@mui/material";
import { PrpHome } from "./PrpHome";

interface PrpNavigationContextType {
  isOpen: boolean;
  toggleNavigation: () => void;
  openNavigation: () => void;
  closeNavigation: () => void;
  navItems: PrpNavigationItemType[];
  handleNavChange: (item: PrpNavigationItemType) => void;
  isMobile: boolean;
  selected: boolean[];
  setSelected: Dispatch<SetStateAction<boolean[]>>;
}

const PrpNavigationContext = createContext<
  PrpNavigationContextType | undefined
>(undefined);

export function usePrpNavigation() {
  const context = useContext(PrpNavigationContext);
  if (context === undefined) {
    throw new Error(
      "usePrpNavigation must be used within a PrpNavigationProvider",
    );
  }
  return context;
}

interface PrpNavigationProviderProps {
  children: ReactNode;
  initialOpen?: boolean;
  navItems?: PrpNavigationItemType[];
  handleNavChange?: (item: PrpNavigationItemType) => void;
}

export function PrpNavigationProvider({
  children,
  navItems = [],
  initialOpen = false,
}: PrpNavigationProviderProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));

  const [isOpen, setIsOpen] = useState(initialOpen);
  const [selected, setSelected] = useState<boolean[]>([]);
  const toggleNavigation = () => setIsOpen((prev) => !prev);
  const openNavigation = () => setIsOpen(true);
  const closeNavigation = () => setIsOpen(false);

  const handleNavChange = (item: PrpNavigationItemType) => {
    if (!item) {
      return;
    }

    if (isOpen) {
      closeNavigation();
    }

    if (item.action) {
      return item.action();
    }
    if (item.path) {
      navigate(item.path);
    }
  };

  return (
    <PrpNavigationContext.Provider
      value={{
        isOpen,
        toggleNavigation,
        openNavigation,
        closeNavigation,
        navItems,
        handleNavChange,
        isMobile: !isSmUp,
        selected,
        setSelected,
      }}
    >
      {children}
      <PrpHome />
    </PrpNavigationContext.Provider>
  );
}
